import AuthContext from "context/AuthContext";
import { useContext, useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  artworkDesignGlobalPermissions,
  artworkMasterDataGlobalPermissions,
} from "routes/master-artwork/helpers/artworkPermissions";
import useAxios from "utils/useAxios";
import {
  DebounceContext,
  DebounceContextType,
} from "../../../../context/DebounceContext";
import Spinner from "../../../../components/Spinner";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { useCreateArtworkCollectionStore } from "../../context/collection/create/CreateArtworkCollectionContext";
import { CreateArtworkCollectionTypes } from "../../state/collection/create/data";
import { CreateCollectionActions } from "../../state/collection/create";
import { useLicensorContext } from "../../../../context/LicensorContext";
import { Predicates } from "../../../../libraries/predicates/predicates";
import useLicensedProperty, {
  fetchLicensedProperty,
} from "../../../../hooks/useLicensedProperty";
import {
  formatLicensedPropertyIntoOption,
  getLicensedPropertys,
} from "../../../../helpers/licensed-property.helper";
import { getTodayDate } from "../../../mpn/features/mpn-create/mpn-create.helper";
import OptionFormatter from "../../../../components/OptionFormatter";
import {
  formatLicensorIntoOption,
  getLicensors,
} from "../../../../helpers/licensor.helper";
import {
  formatRequestorMarketIntoOption,
  getRequestorMarkets,
} from "../../../../helpers/requestor-market.helper";
import useRequestorMarket from "../../../../hooks/useRequestorMarket";
import ArtworkUploadModal from "../../components/artwork-upload-modal";
import ArtworkPictureModal from "../../components/ArtworkPicutureModal";
import ReactModal from "react-modal";
import ArtworkCollectionActionButtonsGroup from "routes/artwork/components/collection-action-buttons/ArtworkCollectionActionButtons";
import { fetchCollection } from "../../helpers/useArtworkFetchCollection";
import { ArtworkRequestHistoryTable } from "../../components/ArtworkRequestHistoryComponent";
import Banner from "../../../../components/Banner";

export const ArtworkCollectionCreatePage = (props: { title: string }) => {
  const axios = useAxios();
  const authContext = useContext(AuthContext);
  const username = authContext?.username;
  const { id } = useParams();
  const location = useLocation();
  const hasCreatorPermission = artworkDesignGlobalPermissions.every(
    (permission) => authContext?.hasPermission(permission),
  );
  const hasApproverPermission = artworkMasterDataGlobalPermissions.every(
    (permission) => authContext?.hasPermission(permission),
  );

  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [isEditing, setIsEditing] = useState<boolean>(
    () => !!urlSearchParams.get("editing") && hasApproverPermission,
  );

  const { searchDebounce } = useContext<DebounceContextType>(DebounceContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { state, dispatch } = useCreateArtworkCollectionStore();

  const { data: requestorMarket, isLoading: isRequestorMarketsLoading } =
    useRequestorMarket();

  const { data: licensors, isLoading: isLicensorsLoading } =
    useLicensorContext();

  const { data: licensedProperties, isLoading: isLicensedPropertiesLoading } =
    useLicensedProperty({
      licensorId: String(state?.licensor?.id?.toString() ?? ""),
    });

  const licensedPropertiesOptions = async (search: string, callback: any) => {
    if (Predicates.isNullOrUndefined(search) || search.length < 3)
      return getLicensedPropertys(licensedProperties);
    const response = await fetchLicensedProperty({
      licensorId: String(String(state?.licensor?.id?.toString() ?? "")),
      search,
      axios,
    });
    callback(getLicensedPropertys(response));
  };

  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [showPictureModal, setShowPictureModal] = useState<boolean>(false);



  const [isSaveOrSubmitInProgress, setIsSaveOrSubmitInProgress] =
    useState<boolean>(false);

  const getMissingConditionsMessage = () => {
    const levelRequired = "full";

    const conditions = [
      {
        condition: (state: any) =>
          state?.name.length > 3 &&
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(state?.name),
        message: `<strong>Name</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) => Predicates.isNotNullAndNotUndefinedAndNotEmpty(state.requesting_market.id),
        message: `<strong>Requesting Market</strong> is required`,
        conditionLevel: "full",
      },
    ];

    const missingConditions = conditions.filter(
      (condition) =>
        (levelRequired === "full"
          ? true
          : condition.conditionLevel === levelRequired) &&
        !condition.condition(state),
    );

    if (missingConditions.length < 1) {
      return "";
    }

    let message =
      "You cannot proceed to collection creation. The following requirements are missing <br> <ul class='mb-0'>";
    message += missingConditions
      .map((condition) => `<li>${condition.message}</li>`)
      .join("");
    return message + "</ul>";
  };

  useEffect(() => {
    const isValid = !getMissingConditionsMessage();

    dispatch(
      CreateCollectionActions.setStateValue(
        CreateArtworkCollectionTypes.SET_IS_VALID, isValid
      ),
    );
  }, [state?.name, state?.requesting_market]);




  const collection = async (id: string | number) => {
    const response = await fetchCollection({ axios, id: id });

    if (response) {
      const isReadOnly =
        //@ts-ignore
        response.status.name === "APPROVED" ||
        //@ts-ignore
        response.status.name === "REJECTED"
          ? true
          //@ts-ignore
          : !hasApproverPermission && response.status.name === "SUBMITTED";

      dispatch(
        CreateCollectionActions.setStateValue(
          CreateArtworkCollectionTypes.SET_ARTWORK_COLLECTION,
          {
            ...response,
            //@ts-ignore
            image_base64: response.image.image_base64,
            isReadOnly: isReadOnly,
          },
        ),
      );
    }
    setIsLoading(false);
  };


  const imageToModal = {
            imageName: `Artwork Collection ${state.code}`,
            image_file: !!state?.code ? null : state.image,
            image_base64:
              !!state?.code && `data:image/jpeg;base64,${state.image_base64}`,
            isBlocked: state.isReadOnly,
          }


  useEffect(() => {
    if (!!id && location?.state?.code) {
      //@ts-ignore
      const isReadOnly =
        location.state.status.name === "APPROVED" ||
        location.state.status.name === "REJECTED"
          ? true
          : !hasApproverPermission &&
            location.state.status.name === "SUBMITTED";

      dispatch(
        CreateCollectionActions.setStateValue(
          CreateArtworkCollectionTypes.SET_ARTWORK_COLLECTION,
          {
            ...location.state,
            image_base64: location.state.image.image_base64,
            isReadOnly: isReadOnly,
          },
        ),
      );
      return
    } else if (!!id) {
      setIsLoading(true);
      collection(id);
    }
     dispatch(CreateCollectionActions.setStateValue(
        CreateArtworkCollectionTypes.RESET, {}
      ))
    setIsEditing(false);
  }, [location, id]);


  return (
    <>
      {isLoading && <Spinner />}
      {!isLoading && (
        <div
          className="container-fluid mt-3 bg-white shadow py-3 rounded-1 d-flex flex-column justify-contnet-between h-100"
          style={{ minHeight: "70vh" }}
        >
          <h6 className="text-primary fw-bold card-header border-0 mb-2">
            {/*Create new Collection*/}
            {props.title}
          </h6>

          <div>
            {/*TODO: make this read the last sequence number and predict the next one as value*/}
            <div className="row form-row align-items-end mb-0">
              <div className="col-lg-1 form-group">
                <label className="form-label">Collection Code</label>
                <input
                  type="text"
                  maxLength={8}
                  className="form-control d-inline"
                  disabled={true}
                  value={state.code}
                  // placeholder="Max 8 characteres (ex: HDTB_SWT)"
                />
              </div>

              <div className="col-lg-4 form-group">
                <label className="form-label">
                  Collection Name <span className="red-text fw-bold">*</span>
                </label>
                <input
                  onChange={(e) => {
                    dispatch(
                      CreateCollectionActions.setStateValue(
                        CreateArtworkCollectionTypes.SET_NAME,
                        e.target.value,
                      ),
                    );
                  }}
                  value={state.name}
                  type="text"
                  className="form-control d-inline"
                  disabled={state?.isReadOnly}
                />
              </div>

              <div className="col-lg-1 form-group">
                <label className="form-label">Collection Year</label>
                <input
                  type="text"
                  maxLength={8}
                  className="form-control d-inline"
                  value={state.year || ""}
                  onChange={(e: any) =>
                    dispatch(
                      CreateCollectionActions.setStateValue(
                        CreateArtworkCollectionTypes.SET_YEAR,
                        e.target.value,
                      ),
                    )
                  }
                  disabled={state?.isReadOnly}
                />
              </div>

              <div className="col-lg-6 form-group">
                <label className="form-label">Collection Licensor</label>
                <Select
                  onChange={(e: any) => {
                    dispatch(
                      CreateCollectionActions.setStateValue(
                        CreateArtworkCollectionTypes.SET_LICENSOR,
                        e ? e.value : null,
                      ),
                    );

                    dispatch(
                      CreateCollectionActions.setStateValue(
                        CreateArtworkCollectionTypes.SET_LICENSED_PROPERTY,
                        null,
                      ),
                    );
                  }}
                  value={formatLicensorIntoOption(state?.licensor)}
                  formatOptionLabel={OptionFormatter}
                  options={getLicensors(licensors)}
                  isClearable={true}
                  classNamePrefix="react-select"
                  isDisabled={state?.isReadOnly}
                />
              </div>
              <div className="col-lg-6 form-group">
                <label className="form-label">Collection License</label>
                <AsyncSelect
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      licensedPropertiesOptions,
                      input,
                      getLicensedPropertys(licensedProperties),
                      callback,
                    );
                  }}
                  defaultOptions={getLicensedPropertys(licensedProperties)}
                  onChange={(e: any) =>
                    dispatch(
                      CreateCollectionActions.setStateValue(
                        CreateArtworkCollectionTypes.SET_LICENSED_PROPERTY,
                        e ? e.value : null,
                      ),
                    )
                  }
                  formatOptionLabel={OptionFormatter}
                  value={formatLicensedPropertyIntoOption(
                    state?.licensed_property,
                  )}
                  classNamePrefix="react-select"
                  placeholder=""
                  isClearable
                  isLoading={isLicensedPropertiesLoading}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={!state?.licensor || state.isReadOnly}
                />
              </div>
              <div className="col-lg-6 form-group">
                <label className="form-label">Collection Launch Date</label>
                <input
                  onChange={(e: any) => {
                    dispatch(
                      CreateCollectionActions.setStateValue(
                        CreateArtworkCollectionTypes.SET_LAUNCH_DATE,
                        e.target.value,
                      ),
                    );
                  }}
                  value={state.launch_date}
                  min={getTodayDate()}
                  type="date"
                  className="form-control d-inline"
                  disabled={state?.isReadOnly}
                />
              </div>

              <div className="col-lg-6 form-group">
                <label className="form-label">
                  Requesting Market <span className="red-text fw-bold">*</span>
                </label>
                <Select
                  options={getRequestorMarkets(requestorMarket)}
                  onChange={(e: any) => {
                    dispatch(
                      CreateCollectionActions.setStateValue(
                        CreateArtworkCollectionTypes.SET_REQUESTING_MARKET,
                        e ? e.value : { full_name: "", id: "", short_name: "" },
                      ),
                    );
                  }}
                  formatOptionLabel={OptionFormatter}
                  value={formatRequestorMarketIntoOption(
                    state.requesting_market,
                  )}
                  classNamePrefix="react-select"
                  placeholder=""
                  isClearable={true}
                  isLoading={isRequestorMarketsLoading}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={state?.isReadOnly}
                />
              </div>

              <div className="col-md-2 form-group align-self-center mb-0">
                <label className="form-label">Artwork Collection Image</label>
                <button
                  onClick={() => {
                    // if(!!state.code && !state.image_base64){
                    //   return
                    // }
                      setShowPictureModal(Predicates.isNotNullAndNotUndefinedAndNotEmpty(state.image_base64));
                      setShowUploadModal(!state.image || !Predicates.isNotNullAndNotUndefinedAndNotEmpty(state.image_base64));
                      // !state.image && setShowUploadModal(true);
                      // !!state.image && setShowPictureModal(true);
                  }}
                  disabled={state.isReadOnly && (!!state.code && !state.image_base64)}
                  type="button"
                  className="icon-button upload-button form-control"
                >
                  {(Predicates.isNotNullAndNotUndefinedAndNotEmpty(state.image?.name) || !!state.image_base64) ? (
                    <img
                      src={
                        process.env.PUBLIC_URL + "/assets/img/uploadSuccess.png"
                      }
                      alt="upload success"
                      className=""
                      width={30}
                    ></img>
                  ) : (
                    <img
                      src={
                        process.env.PUBLIC_URL + "/assets/img/uploadPending.png"
                      }
                      alt="upload pending"
                      className=""
                      width={26}
                    ></img>
                  )}
                </button>
              </div>

            </div>
          </div>

          {state?.history?.length > 0 && (
            <ArtworkRequestHistoryTable historyRecords={state.history} />
          )}

          {!state?.isValid && !state?.isReadOnly && (
            <>
              <Banner
                classname={"mt-3"}
                message={getMissingConditionsMessage()}
                type="danger"
              />
            </>
          )}

          <ArtworkCollectionActionButtonsGroup
            isReadOnlyRequest={state.isReadOnly}
            isEditing={state.isValid}
            isSaveOrSubmitInProgress={isSaveOrSubmitInProgress}
            username={username ?? ""}
            hasApproverPermission={hasApproverPermission}
            hasCreatorPermission={hasCreatorPermission}
            setIsSaveOrSubmitInProgress={setIsSaveOrSubmitInProgress}
          />
        </div>
      )}

      <ReactModal
        isOpen={showUploadModal}
        className="custom-modal barcode-img-modal"
        overlayClassName="custom-overlay"
      >
        <ArtworkUploadModal
          setArtworkImage={(image: any) => {
            dispatch(
              CreateCollectionActions.setStateValue(
                CreateArtworkCollectionTypes.SET_IMAGE,
                image,
              ),
            );
          }}
          uploadedImage={Predicates.isNotNullAndNotUndefinedAndNotEmpty(state.image?.name) ? state.image : null}
          handleCloseModal={() => setShowUploadModal(false)}
        />
      </ReactModal>

      <ReactModal
        isOpen={showPictureModal}
        className="custom-modal mold-comps"
        overlayClassName="custom-overlay"
      >
        <ArtworkPictureModal
          handleCloseModal={() => setShowPictureModal(false)}
          product={imageToModal}
          setImage={(image: any) => {
            dispatch(
              CreateCollectionActions.setStateValue(
                CreateArtworkCollectionTypes.SET_IMAGE,
                image,
              ),
            );
            dispatch(
              CreateCollectionActions.setStateValue(
                CreateArtworkCollectionTypes.SET_IMAGE_BASE64,
                image,
              ),
            );
          }}
        />
      </ReactModal>
    </>
  );
};
